import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { AlertCircle, Loader2 } from 'lucide-react'
import { ReportPager, ReportSection } from '@/components/reports/ReportSection'
import { useProcedureVolumeReport } from '@/hooks/useProcedureVolumeReport'
import { reportFileName, toCsv } from '@/lib/reportCsv'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import { windowLabel, type ReportTimeFrame } from '@/lib/reportRange'
import { PROCEDURE_TYPES, PROCEDURE_TYPE_LABELS, type ProcedureType } from '@/types/cgt'
import type { ProcedureVolumeReport as Report, VolumeBucket } from '@/api/reportsApi'
import { PROCEDURE_TYPE_COLOR } from '@/lib/procedureTypeColors'

/**
 * Report 1.1 — procedure volume by procedure type.
 *
 * Three blocks, the same anatomy the legacy reports screen used: a summary
 * table of the whole window, a chart of its shape over time, and the rows
 * underneath. A reader scans, checks, then drills.
 *
 * ## The chart
 *
 * Stacked bars, one stack per time bucket, one segment per procedure type.
 * Stacked rather than grouped because the question is "how much work, and of
 * what kind" — the bar height is the volume and the segments explain it. Four
 * grouped series over ninety daily buckets would be unreadable.
 *
 * Colours are the validated four-slot categorical palette, assigned by
 * PROCEDURE TYPE in fixed order — never by rank and never cycled. Filtering to
 * two types must not repaint the survivors, or a reader comparing two runs of
 * the same report reads the wrong series.
 *
 * Two of the four slots sit under 3:1 against the surface, which the palette
 * check flags as needing relief: the summary and detail tables are that relief,
 * and both are always present, so identity is never carried by colour alone.
 */

/**
 * Fixed slot per type. Order is the palette's, identity is the TYPE's.
 *
 * Never assigned by rank and never cycled: a reader who filters the report, or
 * compares two runs of it, must find Transplant the same blue both times.
 * Adding a sixth type means taking the next validated slot, not generating one.
 *
 * Validated as a five-slot categorical set against the light chart surface:
 * lightness band, chroma floor, CVD separation and normal-vision floor all
 * pass. Three slots sit under 3:1 contrast, which obliges relief — the summary
 * and detail tables, both always present, and the swatch beside every label.
 */
const SERIES_COLOR: Record<ProcedureType, string> = PROCEDURE_TYPE_COLOR

const DETAIL_PAGE_SIZE = 20

export function ProcedureVolumeReport({
  frame,
  filters,
}: {
  frame: ReportTimeFrame
  filters: ReportFilterSelection
}) {
  const query = useProcedureVolumeReport(frame, filters)
  const [page, setPage] = useState(1)

  if (query.isPending) {
    return (
      <div className='flex items-center gap-2 px-4 py-6 text-xs text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Building the report…
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className='flex items-start gap-2 px-4 py-6 text-xs text-destructive'>
        <AlertCircle className='h-4 w-4 shrink-0' />
        <span>
          <span className='font-medium'>This report could not be built.</span>{' '}
          {query.error instanceof Error ? query.error.message : 'Try a narrower window.'}
        </span>
      </div>
    )
  }

  const report = query.data
  const shown = filters.type?.length ? (filters.type as ProcedureType[]) : [...PROCEDURE_TYPES]

  return (
    <div className='flex flex-col gap-4 p-4'>
      <Totals report={report} />
      <Summary report={report} />
      <VolumeChart report={report} shown={shown} />
      <Details report={report} page={page} onPage={setPage} />
    </div>
  )
}

/**
 * The headline figures.
 *
 * A hero number belongs above a chart that explains it — "how many procedures"
 * is the question this report exists to answer, and a reader should not have to
 * add up a column to find it.
 */
function Totals({ report }: { report: Report }) {
  const completed = report.byType.reduce((sum, t) => sum + t.completed, 0)
  const inProgress = report.byType.reduce((sum, t) => sum + t.inProgress, 0)
  const cancelled = report.byType.reduce((sum, t) => sum + t.cancelled, 0)

  return (
    <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
      <Figure label='Procedures started' value={report.total} tone='text-foreground' />
      <Figure label='Completed' value={completed} tone='text-green-600' />
      <Figure label='In progress' value={inProgress} tone='text-blue-600' />
      <Figure label='Cancelled' value={cancelled} tone='text-muted-foreground' />
    </div>
  )
}

function Figure({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className='rounded-xl border bg-background p-3 shadow-sm'>
      <p className='text-xs text-muted-foreground'>{label}</p>
      <p className={`mt-0.5 text-2xl font-semibold tabular-nums ${tone}`}>{value}</p>
    </div>
  )
}

function Summary({ report }: { report: Report }) {
  const totals = report.byType.reduce(
    (acc, t) => ({
      total: acc.total + t.total,
      completed: acc.completed + t.completed,
      inProgress: acc.inProgress + t.inProgress,
      cancelled: acc.cancelled + t.cancelled,
    }),
    { total: 0, completed: 0, inProgress: 0, cancelled: 0 },
  )

  function exportCsv() {
    download(
      toCsv(
        ['Procedure type', 'Total', 'Completed', 'In progress', 'Cancelled', 'Share %'],
        report.byType.map((t) => [
          PROCEDURE_TYPE_LABELS[t.type] ?? t.type,
          t.total,
          t.completed,
          t.inProgress,
          t.cancelled,
          t.share,
        ]),
      ),
      reportFileName('procedure-volume-summary', report.window, report.grain),
    )
  }

  return (
    <ReportSection
      title='Summary — volume by procedure type'
      subtitle={windowLabel({ from: report.window.from, to: report.window.to })}
      onExport={report.byType.length > 0 ? exportCsv : undefined}
    >
      <div className='overflow-x-persistent'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground'>
              <th className='px-4 py-2 font-medium'>Procedure type</th>
              <Th>Total</Th>
              <Th>Completed</Th>
              <Th>In progress</Th>
              <Th>Cancelled</Th>
              <Th>Share</Th>
            </tr>
          </thead>
          <tbody>
            {report.byType.length === 0 && (
              <tr>
                <td colSpan={6} className='px-4 py-6 text-center text-xs text-muted-foreground'>
                  Nothing started in this window.
                </td>
              </tr>
            )}
            {report.byType.map((t) => (
              <tr key={t.type} className='border-b last:border-0'>
                <td className='px-4 py-2'>
                  <span className='flex items-center gap-2'>
                    {/* The swatch ties the row to its series without the text
                        wearing the series colour. */}
                    <span
                      aria-hidden
                      className='h-2.5 w-2.5 shrink-0 rounded-sm'
                      style={{ background: SERIES_COLOR[t.type] }}
                    />
                    {PROCEDURE_TYPE_LABELS[t.type] ?? t.type}
                  </span>
                </td>
                <Td className='font-medium'>{t.total}</Td>
                <Td>{t.completed}</Td>
                <Td>{t.inProgress}</Td>
                <Td>{t.cancelled}</Td>
                <Td>{t.share}%</Td>
              </tr>
            ))}
          </tbody>
          {report.byType.length > 0 && (
            <tfoot>
              <tr className='border-t bg-muted/30 font-medium'>
                <td className='px-4 py-2'>Total</td>
                <Td>{totals.total}</Td>
                <Td>{totals.completed}</Td>
                <Td>{totals.inProgress}</Td>
                <Td>{totals.cancelled}</Td>
                <Td>—</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {report.untyped > 0 && (
        <p className='border-t px-4 py-2 text-xs text-muted-foreground'>
          {report.untyped} on a template with no type, counted in the total only.
        </p>
      )}
    </ReportSection>
  )
}

function VolumeChart({ report, shown }: { report: Report; shown: ProcedureType[] }) {
  const data = useMemo(
    () =>
      report.buckets.map((b: VolumeBucket) => ({
        bucket: b.bucket,
        label: bucketLabel(b.bucket, report.grain),
        ...b.counts,
      })),
    [report.buckets, report.grain],
  )

  // Series that are zero across the whole window are dropped from the stack —
  // an empty series adds a legend entry that explains nothing. The COLOUR of
  // each survivor is unchanged, which is the rule that matters.
  const series = shown.filter((type) => report.buckets.some((b) => (b.counts[type] ?? 0) > 0))

  return (
    <ReportSection
      title={`Volume over time — ${report.grain}`}
      subtitle={`${report.buckets.length} ${report.grain === 'daily' ? 'days' : report.grain === 'weekly' ? 'weeks' : 'months'}`}
    >
      <div className='h-72 w-full px-2 py-4'>
        {series.length === 0 ? (
          <p className='flex h-full items-center justify-center text-xs text-muted-foreground'>
            Nothing to plot for this window.
          </p>
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
              {/* Horizontal only: vertical lines add nothing over a categorical axis. */}
              <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#e5e7eb' />
              <XAxis
                dataKey='label'
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                interval='preserveStartEnd'
                minTickGap={16}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickLine={false}
                axisLine={false}
                width={32}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}
                formatter={(value, name) => [
                  value as number,
                  PROCEDURE_TYPE_LABELS[name as ProcedureType] ?? String(name),
                ]}
              />
              <Legend
                iconType='square'
                wrapperStyle={{ fontSize: 12 }}
                formatter={(name) => PROCEDURE_TYPE_LABELS[name as ProcedureType] ?? String(name)}
              />
              {series.map((type, i) => (
                <Bar
                  key={type}
                  dataKey={type}
                  stackId='volume'
                  fill={SERIES_COLOR[type]}
                  // A 2px surface gap between stacked segments, and only the
                  // topmost segment gets the rounded data-end.
                  stroke='#fff'
                  strokeWidth={1}
                  radius={i === series.length - 1 ? [4, 4, 0, 0] : 0}
                  maxBarSize={48}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </ReportSection>
  )
}

function Details({
  report,
  page,
  onPage,
}: {
  report: Report
  page: number
  onPage: (page: number) => void
}) {
  const pageCount = Math.max(1, Math.ceil(report.details.length / DETAIL_PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * DETAIL_PAGE_SIZE
  const rows = report.details.slice(start, start + DETAIL_PAGE_SIZE)

  function exportCsv() {
    download(
      toCsv(
        ['Started', 'Procedure type', 'Template', 'Work order', 'Patient MRN', 'Status'],
        report.details.map((d) => [
          d.startedAt,
          d.type ? (PROCEDURE_TYPE_LABELS[d.type] ?? d.type) : '',
          d.templateName,
          d.workOrderNumber,
          d.patientMrn,
          d.status,
        ]),
      ),
      reportFileName('procedure-volume-details', report.window, report.grain),
    )
  }

  return (
    <ReportSection
      title='Details'
      onExport={report.details.length > 0 ? exportCsv : undefined}
      footer={
        <ReportPager
          page={current}
          pageCount={pageCount}
          from={report.details.length === 0 ? 0 : start + 1}
          to={Math.min(start + DETAIL_PAGE_SIZE, report.details.length)}
          total={report.details.length}
          onPage={onPage}
        />
      }
    >
      <div className='overflow-x-persistent'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground'>
              <th className='px-4 py-2 font-medium'>Started</th>
              <th className='px-4 py-2 font-medium'>Type</th>
              <th className='px-4 py-2 font-medium'>Template</th>
              <th className='px-4 py-2 font-medium'>Work order</th>
              <th className='px-4 py-2 font-medium'>Patient</th>
              <th className='px-4 py-2 font-medium'>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className='px-4 py-6 text-center text-xs text-muted-foreground'>
                  Nothing started in this window.
                </td>
              </tr>
            )}
            {rows.map((d) => (
              <tr key={d.id} className='border-b last:border-0'>
                <td className='whitespace-nowrap px-4 py-2 tabular-nums'>
                  {new Date(d.startedAt).toLocaleDateString()}
                </td>
                <td className='px-4 py-2'>
                  {d.type ? (
                    <span className='flex items-center gap-2'>
                      <span
                        aria-hidden
                        className='h-2.5 w-2.5 shrink-0 rounded-sm'
                        style={{ background: SERIES_COLOR[d.type] }}
                      />
                      {PROCEDURE_TYPE_LABELS[d.type] ?? d.type}
                    </span>
                  ) : (
                    <span className='text-muted-foreground'>—</span>
                  )}
                </td>
                <td className='max-w-[18rem] truncate px-4 py-2' title={d.templateName}>
                  {d.templateName}
                </td>
                <td className='whitespace-nowrap px-4 py-2 font-mono text-xs'>
                  {d.workOrderNumber ?? '—'}
                </td>
                <td className='whitespace-nowrap px-4 py-2'>{d.patientMrn ?? '—'}</td>
                <td className='px-4 py-2'>
                  <StatusChip status={d.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {report.detailTotal > report.details.length && (
        <p className='border-t px-4 py-2 text-xs text-muted-foreground'>
          First {report.details.length} of {report.detailTotal} rows. The summary and chart cover
          all {report.detailTotal}.
        </p>
      )}
    </ReportSection>
  )
}

/** Status as a labelled chip — never colour alone. */
function StatusChip({ status }: { status: string }) {
  const tone =
    status === 'completed'
      ? 'bg-green-50 text-green-700'
      : status === 'cancelled'
        ? 'bg-muted text-muted-foreground'
        : 'bg-blue-50 text-blue-700'
  const label =
    status === 'in_progress' ? 'In progress' : status.charAt(0).toUpperCase() + status.slice(1)
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className='px-4 py-2 text-right font-medium'>{children}</th>
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-right tabular-nums ${className}`}>{children}</td>
}

/** Axis label for a bucket: "8 Sep", "w/c 7 Sep", "Sep 2026". */
function bucketLabel(iso: string, grain: Report['grain']): string {
  const d = new Date(`${iso}T00:00:00Z`)
  if (grain === 'monthly') {
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })
  }
  const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })
  return grain === 'weekly' ? `w/c ${day}` : day
}

/**
 * Hand a built CSV to the browser.
 *
 * A blob URL rather than a data: URI — a 500-row export past a few hundred KB
 * trips the URL length limit in some browsers, and the failure is silent.
 */
function download(csv: string, fileName: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}
